import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { DASHBOARD_EDIT_ENTITY_PERMISSIONS } from '../../../utils/apollo/dashboards';
import {
  IDashboardEntityPermissionEditMutation,
  IDashboardEntityPermissionEditMutationVariables,
  IEntityPermissionFieldsFragment,
} from '../../../utils/apollo/graphqlSchema';
import { clipboardDashboardEmbedLink } from '../clipboardDashboardLink';
import {
  IEntityPermissionsWidgetState,
  prepareEntityRoleAssignmentInput,
} from '../utils';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import EntityPermissionsWidgetPopover from '../EntityPermissionsWidgetPopover';
import styled from 'styled-components';
import toastMessage from '../../../utils/toastMessage';
import DashboardShareContent from './DashboardShareContent';
import EmbedCodeContent from '../EmbedCodeContent/EmbedCodeContent';
import { generateEmbedCode } from '../../../utils/embed';

interface IProps {
  dashboardBaseId: string;
  permission: IEntityPermissionFieldsFragment;
  onPermissionsChanged?: (hasChanges: boolean) => void;
}

const Container = styled.div`
  position: relative;
  min-width: 300px;
`;

const DashboardEntityPermissionsWidgetPopover = ({
  dashboardBaseId,
  permission,
  onPermissionsChanged,
}: IProps) => {
  const dashboardEmbedLink = clipboardDashboardEmbedLink(dashboardBaseId);
  const [showEmbedContent, setShowEmbedContent] = useState(false);

  const embedCode = generateEmbedCode(dashboardEmbedLink, {
    title: 'Kubit Dashboard',
  });

  const [editDashboardEntityPermissions] = useMutation<
    IDashboardEntityPermissionEditMutation,
    IDashboardEntityPermissionEditMutationVariables
  >(DASHBOARD_EDIT_ENTITY_PERMISSIONS);

  const handleSubmit = async (formData: IEntityPermissionsWidgetState) => {
    const resp = await editDashboardEntityPermissions({
      variables: {
        input: {
          entityId: permission.entityId,
          entityType: permission.entityType,
          defaultPublicRole: formData.defaultPublicRole,
          roleAssignments: prepareEntityRoleAssignmentInput(formData.users),
        },
      },
    });

    const result = resp?.data?.entityPermissionEdit;
    if (printGraphqlErrors(result?.errors)) {
      return;
    }

    // Notify parent component that permissions have changed
    onPermissionsChanged?.(true);

    toastMessage({
      message: 'User access for the dashboard is updated.',
      type: 'info',
    });
  };

  return (
    <Container>
      <EntityPermissionsWidgetPopover
        permission={permission}
        onSubmit={handleSubmit}
        title="Share this Dashboard"
        showPublicRole={true}
        shareLink={
          <DashboardShareContent setShowEmbedContent={setShowEmbedContent} />
        }
      />
      {showEmbedContent && (
        <EmbedCodeContent
          trackContext="dashboards"
          embedCode={embedCode}
          onClose={() => setShowEmbedContent(false)}
        />
      )}
    </Container>
  );
};

export default DashboardEntityPermissionsWidgetPopover;
